/**
 * The match: the ball, the two sides, and the handful of facts every player has to agree on.
 *
 * There is exactly one of these. It exists because a player deciding what to do needs
 * answers no player owns — whose ball is it, who is going for it, who touched it last and
 * how long ago, and what did the ball look like a reaction-time ago. Those answers have to
 * be computed once a tick, in a fixed order, or twenty-two players will each work from a
 * slightly different picture and the result is jitter that looks like bad AI.
 *
 * THE ORDER MATTERS and is the original's (`Match::Process`, match.cpp:880-930):
 *
 *   ball -> photograph it -> per-player possession -> per-team -> which team, which player
 *   -> then, and only then, the players act
 *
 * Source: match.cpp, team.cpp:203-330 and :488-506, player.cpp:163-300.
 */

import { Vector3 } from 'three';
import { matchDurationFactor, TICK_MS } from '../core/constants';
import { clamp } from '../core/math';
import { hashedRandom } from '../core/random';
import { Ball } from './ball';
import { MentalImageRing, type MentalImage } from './mentalimage';
import {
  allowLastDitch,
  hasPossession,
  teamPossessionAmount,
  timeNeededToGetToBallMs,
  touchOffsetTimeMs,
  type TimeNeeded,
} from './possession';
import { DEFAULT_PLAYER_HEIGHT, DEFAULT_TOUCH_OFFSET_MS } from './anim/touch';
import type { SpatialState } from './anim/spatial';
import type { PlayerPhysics } from './anim/physics';
import { ROLE_ANCHOR, type FormationEntry, type PlayerRole } from './formation';
import { updateTeamAI } from './teamai';
import {
  checkBallCollisions,
  processPlayerCollisions,
  type CollisionBodyPart,
  type CollisionEvent,
} from './collision';
import { MatchPhase, Referee, type RestartState } from './referee';
import { SetPiece } from './setpiece';
import type { FidelityTrace } from './trace';

/** gamedefines.hpp — how a touch is classified, which decides whether a keeper may catch it. */
export enum TouchType {
  None = 0,
  /** Struck with a foot or lower leg. A keeper may not pick these up from his own side. */
  IntentionalKicked = 1,
  /** Chest, head, thigh — deliberate but not a kick. */
  IntentionalNonkicked = 2,
  /** A block, a deflection, a stray boot. */
  Accidental = 3,
}

export { MatchPhase } from './referee';

/**
 * What the match needs from a player's animation runtime.
 *
 * Deliberately narrow and structural, so the match does not depend on the animation system
 * and the two can be tested apart.
 */
export interface PlayerAnimView {
  readonly position: Vector3;
  readonly spatial: SpatialState;
  readonly currentFunctionType: string;
  /** Frame this clip meets the ball on, or -1 if it never does. */
  readonly touchFrame: number;
  /** World-space ball position selected for that contact frame. */
  readonly touchPosition: Vector3;
  readonly frameNum: number;
  /**
   * Standing height in metres. Optional so that a test double need not invent one; absent
   * means the height every clip was drawn for, which is what `HumanoidAnim` itself defaults to.
   */
  readonly standingHeight?: number;
  readonly clip: {
    /** What the clip is called, e.g. `movement/idle/000_idlelevel1`. Fidelity-trace evidence. */
    readonly name: string;
    readonly frameCount: number;
    readonly vars: Readonly<Record<string, string>>;
  };
  /** Collision impulses are applied directly to the animation's root, like OffsetPosition. */
  offsetPosition?(offset: Vector3): void;
  /** Queue a forced fall for the next animation decision. */
  trip?(direction: Vector3, type: number): void;
  /**
   * Queue the forced reaction for a man who has just been punched or flying-kicked — a
   * different clip family from `trip`, which is only a man whose legs met another man's.
   * See tranche 2.3 of `docs/design/violence-and-variety.md`.
   */
  struck?(direction: Vector3): void;
  /** Teleport and idle for a restart. */
  resetPosition?(position: Vector3, focus: Vector3): void;
  /** Enter the stationary retained-ball pose used before a throw-in. */
  selectRetainAnim?(): void;
}

export interface MatchPlayerOptions {
  role?: PlayerRole;
  formationEntry?: FormationEntry;
  collisionBody?: () => CollisionBodyPart[];
  /** Raw `physical_stamina`, 0 to 1. Sets how fast he tires. Defaults to average. */
  stamina?: number;
}

/** How far back the per-second position history reaches, in seconds. */
export const VELOCITY_HISTORY_SECONDS = 10;

/**
 * How long a sent-off player stays worth ticking and drawing, in milliseconds, once
 * `Match.sendOff` has already pulled him out of his side's roster and every gameplay read —
 * AI, collisions, possession. Tranche 1 of `docs/design/violence-and-variety.md`: he used to
 * vanish in the same instant (`main.ts`, `entry.humanoid.object.visible = entry.player.active`).
 * Purely presentational, and layered on top rather than mixed into the dismissal itself — see
 * `MatchPlayer.isDeparting` and `departingPlayerCommands` in `src/sim/controller.ts`.
 *
 * Eight seconds is the worst case rather than the typical one: the furthest an on-pitch man
 * can stand from the nearer touchline is half the pitch's own width, 36 m, which a walk at
 * `WALK_VELOCITY` (5 m/s) covers in 7.2 s. A man dismissed nearer the line reaches it and
 * simply stands there, still drawn, for whatever is left of the window.
 */
export const PLAYER_WALK_OFF_MS = 8000;

export class MatchPlayer {
  /** `Player::active`: false after a red card or second yellow takes effect. */
  active = true;
  /** Yellow = 1, red = 3 (`player.hpp:87-88`). */
  cards = 0;
  /** Cards are shown immediately but do not remove the player for six seconds. */
  cardEffectiveTimeMs = 0;
  /**
   * When `Match.sendOff` took him off, on the match's own clock — null for as long as he is
   * still out there playing. Set once, in the very same call that splices him out of his
   * side's roster; nothing about that call's own timing answers to this field, or to anything
   * that reads it. It exists purely so `isDeparting` can answer how much longer he is still
   * worth drawing.
   */
  dismissedAtMs: number | null = null;
  /** Set by the match every tick. */
  hasPossession = false;
  hasBestPossession = false;
  hasUniquePossession = false;
  /**
   * How long he has had the ball without interruption (`player.cpp:315`). Reset the instant
   * he loses it. Only the keeper reads it, as the four-second limit on how long he may stand
   * there holding it before throwing it out regardless.
   */
  possessionDurationMs = 0;
  timeNeeded: TimeNeeded = { usualMs: 1000, optimisticMs: 1000 };
  closestOpponentDistance = 100;
  side: -1 | 1 = -1;
  formationEntry: FormationEntry;
  dynamicFormationEntry: FormationEntry;
  manMarkingId = -1;
  tacticalSituation = { forwardSpaceRating: 0, spaceRating: 0, forwardRating: 0 };
  /**
   * How fast whoever is driving him is asking him to run RIGHT NOW, metres per second.
   *
   * Not the same as the speed the clip he is playing was chosen for, and the difference is the
   * point: a clip is chosen once and then runs for up to half a second, during which a person
   * can push his stick somewhere else entirely. A carrying touch blends the two — seven parts
   * the wish the clip was started for, three parts this — so that how far he knocks the ball in
   * front of him answers to what is being asked of him at the moment his foot meets it
   * (`elizacontroller.cpp:318`, `humancontroller.cpp:471`, read at
   * `humanoid_utils.cpp:235`).
   */
  controllerVelocityFloat = 0;
  /**
   * Until when this man's challenge counts as violent, on the match's own clock.
   *
   * Set by the flying kick and by nothing else. A lunge is a lunge as far as the animation and
   * the collision are concerned, so the referee has no way to tell one apart from an honest
   * slide by looking at it; this is how he is told. Zero means an ordinary challenge, judged on
   * its merits like everybody else's.
   */
  violentChallengeUntilMs = 0;
  /**
   * The two dribbling flourishes the computer may throw, on the match's own clock.
   *
   * `swerveAllowedFromMs` and `shieldAllowedFromMs` are cooldowns and `shieldingUntilMs` is how
   * long the current shield has left to run. All three are on `Match.timeMs`, which advances
   * every tick INCLUDING while play is stopped — that is what is wanted here, because a man who
   * threw a body swerve just before a throw-in should not come back from it ready to throw
   * another. The football clock would not have moved at all.
   *
   * A person needs none of them: his cooldown is his own finger, and his shield lasts exactly as
   * long as he holds the key.
   */
  swerveAllowedFromMs = 0;
  shieldAllowedFromMs = 0;
  shieldingUntilMs = 0;
  private readonly collisionBodySource: () => CollisionBodyPart[];

  lastTouchTimeMs = 0;
  lastTouchType: TouchType = TouchType.None;

  /**
   * Where he stood at each of the last few whole seconds, newest last.
   *
   * Only the AI's hunting range reads it: a man who has been running hard lately will not set
   * off after an opponent from as far away as one who has been standing about
   * (`player.cpp:145-159`). Cleared on a restart, as the original clears it.
   */
  private readonly positionHistoryPerSecond: Vector3[] = [];

  /**
   * Raw stamina, straight from the database and deliberately NOT read through the modifier
   * every other stat goes through (`player.cpp:328`, `:522-524`). Reading it through the
   * modifier would make a tired player tire faster still, and the drain would run away.
   */
  readonly stamina: number;

  /** 1 fresh, 0.01 spent. Only the match writes it; see `drainFatigue`. */
  private fatigue = 1;

  /**
   * Null while uninjured. Set once by `Match.applyInjury` and never cleared or overwritten —
   * nothing in this invention heals a player mid-match, and a second, lesser knock while
   * already hurt is not worth modelling. See `InjurySeverity`.
   */
  injury: PlayerInjury | null = null;

  /** Where he was at the end of the previous tick, for measuring ground covered. */
  private readonly previousPosition = new Vector3();

  constructor(
    readonly id: number,
    readonly teamId: number,
    readonly anim: PlayerAnimView,
    readonly physics: PlayerPhysics,
    /** Milliseconds of lag between what happens and what he knows. icontroller.cpp:14-16. */
    readonly reactionTimeMs: number,
    options: MatchPlayerOptions = {},
  ) {
    const role = options.role ?? 'CM';
    this.formationEntry = cloneFormationEntry(options.formationEntry ?? defaultFormationEntry(role));
    this.dynamicFormationEntry = cloneFormationEntry(this.formationEntry);
    this.collisionBodySource = options.collisionBody ?? (() => []);
    this.stamina = options.stamina ?? 0.5;
    this.previousPosition.copy(this.position);
  }

  /** 1 fresh, 0.01 spent. */
  get fatigueFactorInv(): number {
    return this.fatigue;
  }

  /**
   * The highest ball he will run to meet, in metres — his own standing height.
   *
   * TWO ROUTINES ASK THIS QUESTION AND THE ORIGINAL ANSWERS IT DIFFERENTLY IN EACH. Working
   * out how long he needs to reach the ball counts every moment of its flight below 1.5 m
   * (`player.cpp:187`); choosing which of those moments to actually run at throws away
   * everything at or above 1.0 m as unattainable (`AIfunctions.cpp:755`). The second search
   * starts where the first one finished, so the lower number governs and the higher one is
   * never felt.
   *
   * NEITHER OF THEM REACHES THE CLIPS. Every header the library ships wants the ball between
   * 1.58 and 1.89 m, drawn for a 1.92 m man. So a header could only ever be played by
   * accident — when the ball happened to fall through a metre at the spot he was already
   * standing on. Measured over four matches before this changed: one header a match, against
   * a chest control every three minutes and a real football's forty-odd headers.
   *
   * So both searches ask this instead, and the answer is the man himself. Scaled to him, the
   * tallest header in the library wants the ball at 1.89 x height / 1.92, which is within two
   * per cent of his height — so his height is the honest ceiling and needs no constant of its
   * own. Taller men win more in the air, which is football and costs nothing to arrange: the
   * reach search already scales every clip's ball height by exactly this ratio.
   */
  get reachableBallHeight(): number {
    return this.anim.standingHeight ?? DEFAULT_PLAYER_HEIGHT;
  }

  /**
   * Take the ground covered this tick out of him, `player.cpp:327-329`.
   *
   * Divided by the match-duration factor, so tiredness is paced against the football clock
   * rather than the wall clock: a short match tires players just as much per displayed minute
   * as a long one. A low-stamina player drains up to twice as fast as a high-stamina one.
   */
  drainFatigue(durationFactor: number): void {
    const distance = this.position.distanceTo(this.previousPosition);
    this.previousPosition.copy(this.position);
    this.setFatigue(this.fatigue - distance * 0.00003 * (2 - this.stamina) * (1 / durationFactor));
  }

  /** `PlayerBase::RelaxFatigue` — a small recovery, applied at half time. */
  relaxFatigue(amount: number): void {
    this.setFatigue(this.fatigue + amount);
  }

  private setFatigue(value: number): void {
    this.fatigue = clamp(value, 0.01, 1);
    // Published so every stat read scales itself; see PlayerPhysics.fatigueFactorInv.
    this.physics.fatigueFactorInv = this.fatigue;
  }

  /**
   * Record an injury and publish its skill penalty, exactly as `setFatigue` publishes
   * `physics.fatigueFactorInv`: the instant `Match.applyInjury` decides an injury, not on
   * whatever later tick a stat happens to be read.
   */
  setInjury(injury: PlayerInjury): void {
    this.injury = injury;
    this.physics.injuryFactorInv = INJURY_SKILL_FACTOR[injury.severity];
  }

  /** A teleport is not running: don't charge him for it. */
  forgetPreviousPosition(): void {
    this.previousPosition.copy(this.position);
  }

  get position(): Vector3 {
    return this.anim.position;
  }

  get movement(): Vector3 {
    return this.anim.spatial.movement;
  }

  /** Called once a second by the match. */
  recordPositionSample(historySeconds: number): void {
    this.positionHistoryPerSecond.push(this.position.clone());
    if (this.positionHistoryPerSecond.length > historySeconds + 1) {
      this.positionHistoryPerSecond.shift();
    }
  }

  clearPositionHistory(): void {
    this.positionHistoryPerSecond.length = 0;
  }

  /**
   * Metres per second averaged over the last `seconds` seconds.
   *
   * Deliberately divided by the whole period rather than by the samples actually held, which
   * is what the original does: early in a match, with little history, everyone reads as
   * slower than they are, and the hunting range is correspondingly generous.
   */
  averageVelocity(seconds: number): number {
    const history = this.positionHistoryPerSecond;
    if (history.length === 0) return 0;
    let distance = 0;
    for (let step = 1; step <= seconds && step < history.length; step++) {
      const newer = history[history.length - step]!;
      const older = history[history.length - step - 1]!;
      distance += newer.distanceTo(older);
    }
    return distance / seconds;
  }

  get directionVec(): Vector3 {
    return this.anim.spatial.directionVec;
  }

  get bodyDirectionVec(): Vector3 {
    return this.anim.spatial.bodyDirectionVec;
  }

  get floatVelocity(): number {
    return this.anim.spatial.floatVelocity;
  }

  get role(): PlayerRole {
    return this.formationEntry.role;
  }

  get dynamicRole(): PlayerRole {
    return this.dynamicFormationEntry.role;
  }

  get maxVelocity(): number {
    return this.physics.maxVelocity;
  }

  touchPending(): boolean {
    return this.anim.frameNum < this.anim.touchFrame;
  }

  collisionBody(): CollisionBodyPart[] {
    return this.collisionBodySource();
  }

  giveYellowCard(effectiveTimeMs: number): void {
    this.cards += 1;
    this.cardEffectiveTimeMs = effectiveTimeMs;
  }

  giveRedCard(effectiveTimeMs: number): void {
    this.cards += 3;
    this.cardEffectiveTimeMs = effectiveTimeMs;
  }

  /**
   * 1 the instant he touched the ball, fading to 0 over `decayMs` — playerbase.cpp:141-146.
   *
   * `atMs` lets a caller ask about a moment in the future, which is how the reach test knows
   * a player who has only just touched the ball cannot touch it again three frames later.
   */
  lastTouchBias(decayMs: number, atMs: number): number {
    if (decayMs <= 0) return 0;
    return 1 - Math.min(Math.max((atMs - this.lastTouchTimeMs) / decayMs, 0), 1);
  }

  /**
   * Still worth ticking and drawing, as of `atMs` on the match's own clock — normally
   * `Match.timeMs`, the current tick? False for as long as he has never been dismissed, since
   * there is no departure to speak of; true for `PLAYER_WALK_OFF_MS` after `dismissedAtMs`, so
   * the render side can carry him walking for the touchline instead of cutting him out on the
   * very tick `Match.sendOff` fires — see that constant for why this particular number.
   */
  isDeparting(atMs: number): boolean {
    return this.dismissedAtMs !== null && atMs - this.dismissedAtMs < PLAYER_WALK_OFF_MS;
  }
}

export class TeamState {
  score = 0;
  hasPossession = false;
  timeNeededToGetToBallMs = 100000;
  possessionAmount = 1;
  fadingPossessionAmount = 1;
  offsideTrapX = 0;
  attackingRunPlayer: MatchPlayer | null = null;
  attackingRunUntilMs = 0;
  forwardSupportPlayer: MatchPlayer | null = null;

  /**
   * The second half of a wall pass ("pared"): which team-mate the ball has just been played to
   * as this side's nominated run, who he owes the ball back to, and how long that still holds.
   *
   * Set only by `armWallPassReturn` (`src/input/player.ts`), which fires only from a human's
   * own short pass to his own live `attackingRunPlayer` nomination — never by the computer's
   * own play. `updateAttackingRun` below nominates a run for the AI too, on these same two
   * fields above, but nothing in the computer's own command building ever arms these three, so
   * an AI-versus-AI match cannot reach `wallPassReturnCommand` (`src/sim/controller.ts`) at
   * all. Read once, by that function, and left to time out on its own rather than consumed
   * explicitly — the reach search already refuses an unreachable ball, and a clip already in
   * progress cannot be re-queued mid-touch, so nothing has to remember that this already fired.
   */
  wallPassReceiver: MatchPlayer | null = null;
  wallPassPasser: MatchPlayer | null = null;
  wallPassUntilMs = -1;
  /**
   * How far forward this side is willing to commit, 0 to 1. Recomputed once a second from the
   * scoreline, the clock and who has had the ball; see `offensivenessBias`. Even at kick-off.
   */
  offensiveness = 0.5;
  tacticsUpdatedAtMs = -1000;

  /**
   * A squeeze ordered from the touchline: for half a second after the button, one nominated
   * player abandons his shape and goes straight at whoever has the ball, and is given him to
   * mark besides. Half a second sounds short, but the order is meant to be held down.
   * `teamAIcontroller.cpp:930-945`.
   */
  teamPressureUntilMs = -1;
  teamPressurePlayer: MatchPlayer | null = null;

  /** Three tenths of a second of "come and get it" for the keeper. `:947-949`. */
  keeperRushUntilMs = -1;

  /**
   * Until when this side is under the all-out order, on the match's own clock.
   *
   * Ours, not the original's — it has no such order. Set only by a person holding his own key
   * down (`applyAllOut`, `src/input/player.ts`) and read only by `underAllOutOrder`
   * (`src/sim/controller.ts`), which is what makes it unreachable in an unattended match: it
   * starts before the clock does, so nothing is ever under it until somebody presses something.
   */
  allOutUntilMs = -1;

  dynamicRolesUpdatedAtMs = -400;
  manMarkingUpdatedAtMs = -300;
  attackingRunCheckedAtMs = -500;
  forwardSupportUpdatedAtMs = -1500;
  /** The one man this side has decided is going for the ball. */
  designatedPlayer: MatchPlayer;
  lastTouchPlayer: MatchPlayer | null = null;
  lastTouchTimeMs = 0;
  lastTouchType: TouchType = TouchType.None;

  constructor(
    readonly id: number,
    /** -1 defends the negative-x goal, +1 the positive one. team.cpp:102-113. */
    side: -1 | 1,
    readonly players: MatchPlayer[],
  ) {
    this.initialSide = side;
    this.side = side;
    this.designatedPlayer = players[0]!;
    for (const player of players) player.side = side;
  }

  readonly initialSide: -1 | 1;
  side: -1 | 1;

  setSide(side: -1 | 1): void {
    this.side = side;
    for (const player of this.players) player.side = side;
  }

  lastTouchBias(decayMs: number, nowMs: number): number {
    if (decayMs <= 0) return 0;
    return 1 - Math.min(Math.max((nowMs - this.lastTouchTimeMs) / decayMs, 0), 1);
  }
}

export interface MatchOptions {
  ball: Ball;
  teams: [TeamState, TeamState];
  /** Menu value, converted by `match.cpp:52` to the displayed-clock rate. */
  matchDuration?: number;
  /** Production begins with the original pre-match referee kick-off protocol. */
  initialKickOff?: boolean;
  /**
   * Menu value, 0 to 1. Beyond the stat and reaction-time handicaps applied when a side has
   * no human on it, this is what decides how far an AI player will travel to close somebody
   * down (`elizacontroller.cpp:347`). The original's own default is the middle setting.
   */
  difficulty?: number;
  /**
   * The seed every chance in this match is derived from, so a run can be repeated exactly.
   *
   * Zero is the default and reproduces the behaviour this game had before it was seedable at
   * all: the same two teams play the same match every time. Any other value plays a different
   * match, and the same value plays that one again.
   */
  seed?: number;
  /** See `Match.violence`. Defaults to the ordinary match. */
  violence?: Violence;
}

/**
 * Which of the two matches this is.
 *
 * `'match'` is football. Every violent act a person can perform exists and every one of them is
 * punishable; what makes them worth doing is that the referee is a man with a position and a
 * field of view, so an offence committed far from him usually goes unseen.
 *
 * `'anything-goes'` is the same football underneath and the same pitch, with the referee no
 * longer judging violence at all, an exaggerated register of the same clips, and injuries
 * commoner.
 *
 * NOT RECORDED IN THE TRACE, on purpose. The trace speaks the original's own format and a test
 * pins its header byte for byte against a real recording of the original
 * (`tests/trace.test.ts`), which has no notion of a mode and never will. So two recordings made
 * in different modes are indistinguishable by their headers, and the measuring bench reports
 * the mode instead. Anything comparing two recordings has to know it made them both the same
 * way; that is the price of the header staying honest about what it is.
 */
export type Violence = 'match' | 'anything-goes';

/**
 * How badly a player is hurt — tranche 2.4 of `docs/design/violence-and-variety.md`.
 *
 * Invention: nothing of the kind exists in the original, and the shape is kept to what the
 * plan actually asks for and no more. 1 and 2 keep him on the pitch with his skills scaled
 * down for the rest of the match — nothing here heals him — and 3 is serious enough to end
 * it outright, through the SAME dismissal path a red card uses (`Match.applyInjury`,
 * `Match.sendOff`). There is deliberately no fourth thing that follows from 3: a substitute
 * is out of scope, and the reason is in the data rather than the code — the importer keeps
 * one goalkeeper per club and deletes the rest, so a side whose keeper is hurt off has no
 * keeper to bring on, and building a bench for outfield players only would be answering a
 * question the owner did not ask.
 */
export type InjurySeverity = 1 | 2 | 3;

export interface PlayerInjury {
  readonly severity: InjurySeverity;
  /** `Match.timeMs` when it happened. */
  readonly timeMs: number;
}

/**
 * The skill penalty for each severity, read through the SAME multiplication tiredness
 * already goes through — `PlayerPhysics.injuryFactorInv`, wired into the one `stat` closure
 * in `main.ts`. Chosen rather than measured (there is nothing in the original to measure
 * against): a mild knock is a lesser version of being tired, a serious one is nearly a
 * passenger for what little of the match he has left before `Match.applyInjury` sends him
 * off. 1.0 would be indistinguishable from uninjured; there is no fourth entry because
 * nothing produces a severity higher than 3.
 */
const INJURY_SKILL_FACTOR: Readonly<Record<InjurySeverity, number>> = {
  1: 0.85,
  2: 0.6,
  3: 0.3,
};

/**
 * Injury-chance tuning, per QUALIFYING collision (`Match.assessInjury` below) — tranche 2.4.
 * Chosen, not measured (there is nothing to measure against), then checked against a
 * twenty-four-match run for the one thing that matters: sides still finishing with ten men
 * rather than seven. A type-1 trip — the mildest of the three, a stumble rather than a fall —
 * carries no chance at all; only 2 and 3 do, and 3 (a sliding-tackle-grade foul) is five
 * times likelier than 2. A landed punch or a flying kick is rarer than either but far more
 * dangerous when it lands — the whole point of the act — and reachable only through
 * `Match.assessInjury`'s own violent branch, which both the flying kick and the punch reach
 * by setting `violentChallengeUntilMs` — the same field the referee reads to tell a lunge at a
 * man apart from an honest challenge.
 */
const INJURY_CHANCE_TRIP2 = 0.004;
const INJURY_CHANCE_TRIP3 = 0.02;
const INJURY_CHANCE_VIOLENT = 0.35;
/** `Match.violence === 'anything-goes'` multiplies every one of the chances above by this. */
const ANYTHING_GOES_INJURY_MULTIPLIER = 3;
/** Centre of `mental_resilience`'s in-play range (0.52–0.88, docs/design/data.md), so the
 * per-player multiplier below averages out to 1.0 across the league and none of the three
 * constants above has to move. Chosen, not measured. */
const RESILIENCE_REFERENCE = 0.7;
/** How much the victim's own `mental_resilience` shifts his injury chance either side of the
 * three constants above — a ±25% swing between the toughest (0.88) and most fragile (0.52)
 * player in play. Chosen, not measured. */
const RESILIENCE_INJURY_SLOPE = 1.4;
/** Once an injury fires, how its severity is drawn. Shares, not a formula: mild dominates,
 * serious — the one that ends his match — is meant to stay rare. */
const INJURY_SEVERITY_SHARE: Readonly<Record<InjurySeverity, number>> = {
  1: 0.6,
  2: 0.3,
  3: 0.1,
};
/** Salts for the two draws `assessInjury` needs off the same tick and player: whether an
 * injury happens at all, and if so how severe. Distinct salts so the two answers do not move
 * together (`hashedRandom`'s own doc comment). */
const INJURY_CHANCE_SALT = 401;
const INJURY_SEVERITY_SALT = 402;

export class Match {
  readonly ball: Ball;
  readonly teams: [TeamState, TeamState];
  readonly difficulty: number;
  /** See the type. The default is football, and every existing measurement is on that setting. */
  readonly violence: Violence;
  /** See `MatchOptions.seed`. Every derived coin flip in the match mixes this in. */
  readonly randomSeed: number;
  /**
   * The fidelity recorder, or null when nothing is being recorded — which is the normal case
   * and costs one null check a tick. Set it and every tick is sampled from then on.
   */
  trace: FidelityTrace | null = null;
  private readonly allPlayers: MatchPlayer[];
  private readonly durationFactor: number;

  /** Simulation wall-clock. Advances by exactly one tick a tick; never real delta time. */
  timeMs = 0;
  /** Displayed football clock, accelerated by `1 / matchDurationFactor`. */
  matchTimeMs = 0;
  phase: MatchPhase;
  inPlay = true;
  inSetPiece = false;
  /** The original ends the match when either side has six or fewer active players. */
  gameOver = false;
  lastGoalTeamId = -1;
  /** Incremented whenever a restart teleports the scene, so render interpolation can reset. */
  resetSerial = 0;

  /**
   * The single player the whole match treats as the focus of play.
   *
   * Not simply the nearest man: switching is deliberately sticky, because a focus that
   * flickers between two players a metre apart makes the camera, the AI and the requeue
   * rate limiter all twitch at once.
   */
  designatedPossessionPlayer: MatchPlayer;
  /** -1 when neither side is closer. */
  bestPossessionTeamId: -1 | 0 | 1 = -1;
  /** A keeper who has the ball in his hands. Nobody else may touch it while this is set. */
  ballRetainer: MatchPlayer | null = null;

  lastTouchTeamId = 0;

  /**
   * Is a person driving this player? Answered from outside, because nothing in the simulation
   * knows: the device-to-player mapping lives with the input code, and on its own the match
   * assumes every player is the computer's, which is what an unattended match is.
   *
   * It matters in exactly one place so far — deciding whether a side hands the job of going
   * for the ball to somebody else, where the original nudges the sums both ways around a
   * human (`Team::IsHumanControlled`, team.cpp:188-193 and :424-428). Whatever owns the
   * controllers should set this when a match starts.
   */
  humanControlled: (player: MatchPlayer) => boolean = () => false;

  /**
   * Which side last made a touch of each kind, kept quite separately from who touched it last
   * of all (`match.hpp:149-150`, one slot per touch type).
   *
   * Only the goalkeeper's back-pass rule reads it, and it is the whole of that rule: once his
   * own side has deliberately kicked the ball, he may not pick it up, and a stray touch off
   * somebody in between does not wash that away. Asking the single most recent touch instead
   * would let a team-mate's accidental deflection hand him the ball legally.
   */
  private readonly lastTouchTeamIdByType: number[] = [-1, -1, -1, -1];
  readonly collisionEvents: CollisionEvent[] = [];
  /** When the ball last came off somebody's body, so it cannot do so again for 150 ms. */
  lastBodyBallCollisionTimeMs = -1000;
  private readonly possessionSideHistory: number[] = [];

  /** Leaky integrator behind `recentPossessionFactor`; ±60 is a minute of one-sided play. */
  private possessionLeak = 0;

  private readonly mentalImages: MentalImageRing;
  readonly referee: Referee;

  constructor(options: MatchOptions) {
    this.ball = options.ball;
    this.teams = options.teams;
    this.difficulty = options.difficulty ?? 0.5;
    this.violence = options.violence ?? 'match';
    this.randomSeed = (options.seed ?? 0) >>> 0;
    this.durationFactor = matchDurationFactor(options.matchDuration ?? 0.4);
    this.allPlayers = [...options.teams[0].players, ...options.teams[1].players];
    this.designatedPossessionPlayer = this.allPlayers[0]!;
    this.mentalImages = new MentalImageRing(this.ball, this.allPlayers);
    this.referee = new Referee(this.ball.position);
    this.phase = options.initialKickOff ? MatchPhase.PreMatch : MatchPhase.FirstHalf;
    this.inPlay = !options.initialKickOff;
    if (options.initialKickOff) this.referee.startMatch(this);
  }

  get setPiece(): SetPiece {
    return this.referee.setPiece;
  }

  /** The live roster. Dismissed players stay addressable through render-side references only. */
  get players(): MatchPlayer[] {
    return [...this.teams[0].players, ...this.teams[1].players];
  }

  get setPieceTaker(): MatchPlayer | null {
    return this.referee.taker;
  }

  get pendingRestart(): RestartState | null {
    return this.referee.restart;
  }

  /** Keeper retention and the throw-in's animation-only retained state share this contract. */
  get retainedBallPlayer(): MatchPlayer | null {
    if (this.ballRetainer) return this.ballRetainer;
    const restart = this.referee.restart;
    return restart?.type === SetPiece.ThrowIn && restart.prepared ? restart.taker : null;
  }

  isRetainingBall(player: MatchPlayer): boolean {
    return this.retainedBallPlayer === player;
  }

  team(id: number): TeamState {
    return this.teams[id === 0 ? 0 : 1]!;
  }

  /** The other side. */
  opponentTeam(id: number): TeamState {
    return this.teams[id === 0 ? 1 : 0]!;
  }

  /**
   * What a player believes, given how long he takes to notice things.
   *
   * A player who touched the ball himself is granted the truth: he does not need to see a
   * pass being struck to know he struck it (playercontroller.cpp:36-41).
   */
  mentalImageFor(player: MatchPlayer): MentalImage {
    const ownTouch =
      this.lastTouchPlayer() === player && player.lastTouchType !== TouchType.Accidental;
    return this.mentalImages.get(ownTouch ? 0 : player.reactionTimeMs);
  }

  mentalImage(historyMs = 0): MentalImage {
    return this.mentalImages.get(historyMs);
  }

  averagePossessionSide(durationMs: number): number {
    const count = Math.min(this.possessionSideHistory.length, Math.floor(durationMs / TICK_MS) + 1);
    if (count === 0) return 0;
    let total = 0;
    for (let i = this.possessionSideHistory.length - count; i < this.possessionSideHistory.length; i++) {
      total += this.possessionSideHistory[i]!;
    }
    return total / count;
  }

  lastTouchPlayer(): MatchPlayer | null {
    return this.team(this.lastTouchTeamId).lastTouchPlayer;
  }

  /** Which side last touched the ball in this particular way, or -1 if nobody yet. */
  lastTouchTeamIdOfType(type: TouchType): number {
    return this.lastTouchTeamIdByType[type] ?? -1;
  }

  /** `Match::ResetSituation` (`match.cpp:661-663`) wipes the record at every restart. */
  clearTouchRecord(): void {
    this.lastTouchTeamIdByType.fill(-1);
  }

  /** Record a touch. `Team::SetLastTouchPlayer`. */
  setLastTouchPlayer(player: MatchPlayer, type: TouchType): void {
    if (!player.active) return;
    const team = this.team(player.teamId);
    team.lastTouchPlayer = player;
    team.lastTouchTimeMs = this.timeMs;
    team.lastTouchType = type;
    player.lastTouchTimeMs = this.timeMs;
    player.lastTouchType = type;
    this.lastTouchTeamId = player.teamId;
    this.lastTouchTeamIdByType[type] = player.teamId;
    this.referee.ballTouched(this, player);
  }

  /** A touch changed the future, so the newest photograph has to be retaken. */
  refreshBallPredictions(): void {
    this.mentalImages.refreshLatest();
  }

  /**
   * Advance the ball and everything derived from it. Call once a tick, BEFORE the players.
   */
  step(): void {
    this.timeMs += TICK_MS;
    // Before the referee, so a shot deflected behind is already the defender's touch by the
    // time he decides between a corner and a goal kick, and before the ball moves, so the
    // deflection is applied to the position the bodies were posed against (`match.cpp:868`).
    if (this.inPlay && !this.gameOver) checkBallCollisions(this);
    this.referee.process(this);
    this.processDismissals();
    if (this.gameOver) return;
    this.ball.process();
    this.mentalImages.advance();
    this.updatePossession();
    if (this.inPlay) {
      for (const player of this.allPlayers) {
        player.possessionDurationMs = player.hasPossession ? player.possessionDurationMs + TICK_MS : 0;
      }
    }
    updateTeamAI(this);
    if (this.inPlay && this.bestPossessionTeamId >= 0) {
      const sideValue = this.teams.reduce(
        (sum, team) => sum + (team.fadingPossessionAmount - 0.5) * team.side,
        0,
      );
      this.possessionSideHistory.push(sideValue);
      if (this.possessionSideHistory.length > 600) this.possessionSideHistory.shift();
    }
    if (this.inPlay && !this.inSetPiece) {
      this.matchTimeMs += TICK_MS * (1 / this.durationFactor);
      // Who has been on the ball lately, as a slow leak towards whichever side holds it
      // (`matchdata.cpp:28-33`). Saturates after a minute of one-sided play, and note it
      // counts real seconds, not clock minutes.
      const holder = this.designatedPossessionPlayer.teamId;
      this.possessionLeak = clamp(this.possessionLeak + (holder === 0 ? -0.01 : 0.01), -60, 60);
    }
  }

  /**
   * Who has had the ball over the last minute: 0 is all team one, 1 is all the other side,
   * 0.5 is even. `matchdata.hpp:25`.
   */
  get recentPossessionFactor(): number {
    return (this.possessionLeak / 60) * 0.5 + 0.5;
  }

  /** Apply the phase's coordinate convention before its kick-off formation is prepared. */
  setPhase(phase: MatchPhase): void {
    this.phase = phase;
    // Put the football clock back to where the new period starts (`match.cpp:687-693`).
    // A half does not end the moment the clock passes forty-five minutes: the referee waits
    // for the ball to come back near the halfway line first, and the displayed clock runs
    // several times faster than real time, so a few seconds of waiting is half a minute of
    // football. Left alone, that overrun comes straight out of the next period, which then
    // plays short — and the shortfall accumulates all the way into extra time.
    const start = PHASE_START_MS[phase];
    if (start !== undefined) this.matchTimeMs = start;
    const swapped = phase === MatchPhase.SecondHalf || phase === MatchPhase.SecondExtraTime;
    for (const team of this.teams) {
      team.setSide((swapped ? -team.initialSide : team.initialSide) as -1 | 1);
    }
    // The interval. Five percentage points back on a scale a hard-working player spends over
    // ninety minutes, and only at half time — nowhere else does anybody get their breath back
    // (`match.cpp:695-698`). It is not meant to be generous.
    if (phase === MatchPhase.SecondHalf) {
      for (const player of this.allPlayers) player.relaxFatigue(0.05);
    }
  }

  endMatch(): void {
    this.gameOver = true;
    this.inPlay = false;
    this.inSetPiece = false;
    this.ballRetainer = null;
    this.referee.gameOver();
  }

  /** Run after all 22 animation ticks, matching Match::CheckHumanoidCollisions' phase. */
  finishStep(): void {
    if (!this.gameOver) {
      if (this.timeMs % 1000 === 0) {
        for (const player of this.allPlayers) player.recordPositionSample(VELOCITY_HISTORY_SECONDS);
      }
      for (const player of this.allPlayers) player.drainFatigue(this.durationFactor);
      this.collisionEvents.length = 0;
      processPlayerCollisions(this, this.collisionEvents);
      for (const event of this.collisionEvents) {
        const victim = this.players.find((player) => player.id === event.victimId);
        const tripper = this.players.find((player) => player.id === event.tripperId);
        if (victim && tripper) {
          // BEFORE the referee, not after: a flying kick's `violentChallengeUntilMs` is a
          // ONE-SHOT signal that `tripNotice` itself zeroes the instant it books it
          // (`referee.ts:293-295`), so an injury check running after it would always read
          // an honest challenge, no matter how the tackler was actually playing.
          this.assessInjury(victim, tripper, event.tripType);
          this.referee.tripNotice(this, victim, tripper, event.tripType);
        }
      }
    }

    // THE SAMPLE POINT, and the reason it lives here rather than in the game loop: the
    // original takes its snapshot as the last act of `Match::Process`, after the referee, the
    // ball, all twenty-two players, the collisions and the clocks, and immediately before its
    // iteration counter increments (`match.cpp:1133` with the tracing patch applied). This is
    // that same instant on this side — nothing after it changes the state of the simulation.
    // A snapshot taken one step earlier or later is still well formed and compares wrongly.
    this.trace?.writeTick(this);
  }

  /**
   * Recompute who is going for the ball — match.cpp:900-925, team.cpp:400-440.
   *
   * The whole thing is one long exercise in not changing its mind too easily. A player only
   * takes over from his team's designated man if he is 20% quicker; a team only takes over
   * if its man is 15% quicker than the current focus. Without that hysteresis the focus
   * oscillates every few ticks and everything downstream shakes.
   */
  private updatePossession(): void {
    for (const team of this.teams) {
      for (const player of team.players) {
        const precise = team.designatedPlayer === player;
        player.timeNeeded = timeNeededToGetToBallMs(this.ball, player, precise);
        player.hasPossession = hasPossession(this.ball, player);
        player.closestOpponentDistance = this.closestOpponentDistanceFor(player);
      }
    }

    // A player already committed to a touch will get there when his foot gets there,
    // whatever the geometry says (player.cpp:257-262).
    for (const player of this.players) {
      if (player.anim.touchFrame !== -1 && player.touchPending()) {
        const animTimeMs = (player.anim.touchFrame - player.anim.frameNum) * 10;
        if (animTimeMs < player.timeNeeded.usualMs) {
          player.timeNeeded = { usualMs: animTimeMs, optimisticMs: animTimeMs };
        }
      }

      // ...and below the eighty milliseconds a touch takes to land, the scan has no
      // resolution left and answers ten for everybody, so the geometry decides instead
      // (player.cpp:262-266). See `touchOffsetTimeMs`. Runs after the committed-touch
      // override, as the original runs it, so a man whose foot is already on its way keeps
      // the time his animation promises unless that is itself under eighty.
      if (player.timeNeeded.usualMs < DEFAULT_TOUCH_OFFSET_MS) {
        const rescaled = touchOffsetTimeMs(this.ball, player);
        player.timeNeeded = { usualMs: rescaled, optimisticMs: rescaled };
      }
    }

    for (const team of this.teams) {
      team.hasPossession = false;
      team.timeNeededToGetToBallMs = 100000;
      for (const player of team.players) {
        if (player.hasPossession) team.hasPossession = true;
        if (player.timeNeeded.usualMs < team.timeNeededToGetToBallMs) {
          team.timeNeededToGetToBallMs = player.timeNeeded.usualMs;
        }
      }
    }

    // Both sides' figures are final before either side reconsiders who is going for the
    // ball, so neither reads a number the other has already moved on from.
    for (const team of this.teams) {
      team.possessionAmount = teamPossessionAmount(
        team.timeNeededToGetToBallMs,
        this.opponentTeam(team.id).timeNeededToGetToBallMs,
      );
      this.updateDesignatedPlayer(team);
    }

    // A player with a keeper's hands on the ball is nobody's to chase.
    for (const player of this.players) {
      if (this.ballRetainer === player) {
        player.hasPossession = true;
        player.hasBestPossession = true;
        player.hasUniquePossession = true;
        player.timeNeeded = { usualMs: 1, optimisticMs: 1 };
      } else if (this.ballRetainer !== null) {
        player.hasPossession = false;
        player.hasBestPossession = false;
        player.hasUniquePossession = false;
      } else {
        const opponents = this.opponentTeam(player.teamId);
        player.hasBestPossession =
          player.hasPossession && opponents.timeNeededToGetToBallMs > player.timeNeeded.usualMs;
        player.hasUniquePossession = player.hasPossession && !opponents.hasPossession;
      }
    }

    this.calculateBestPossessionTeamId();

    if (this.ballRetainer !== null) {
      this.designatedPossessionPlayer = this.ballRetainer;
    } else if (this.bestPossessionTeamId !== -1) {
      const candidate = this.team(this.bestPossessionTeamId).designatedPlayer;
      if (candidate !== this.designatedPossessionPlayer) {
        const timeRating =
          (candidate.timeNeeded.usualMs + 10) /
          (this.designatedPossessionPlayer.timeNeeded.usualMs + 10);
        if (timeRating < 0.85) this.designatedPossessionPlayer = candidate;
      }
    } else {
      this.designatedPossessionPlayer = this.team(
        this.designatedPossessionPlayer.teamId,
      ).designatedPlayer;
    }
  }

  /**
   * Should this side hand the job of going for the ball to somebody else? `team.cpp:407-433`.
   *
   * The man already on the errand keeps it unless the challenger is comfortably quicker —
   * the original's own comment on the test is "switch only if other player is somewhat
   * better, to overcome possession-chaos". Four thumbs are on the scale before the
   * comparison. Whoever actually has the ball at his feet counts double, either way round.
   * Whoever a person is driving is favoured a little, because the game would rather the man
   * on the controller were the one running at the ball.
   *
   * And then the big one: a side already winning the race to the ball hardly ever switches,
   * because there is nothing to fix. Being a tenth of a second clear of the nearest opponent
   * is enough to move the bar from 0.8 down to 0.467, so a man 800 ms from the ball keeps the
   * errand against a team-mate half that distance away. Without it a side re-elects its
   * chaser every few ticks and two or three men take turns setting off after every loose
   * ball — and since this is also the man the opposition close down and the only one granted
   * the wide time-to-ball scan, everything downstream twitches with him.
   */
  private updateDesignatedPlayer(team: TeamState): void {
    const best = bestChaser(team);
    if (team.designatedPlayer === best) return;
    const designated = team.designatedPlayer;
    const designatedTimeMs = designated.timeNeeded.usualMs;
    let timeRating = (best.timeNeeded.usualMs + 500) / (designatedTimeMs + 500);
    if (best.hasPossession) timeRating *= 0.5;
    if (designated.hasPossession) timeRating /= 0.5;
    const bestIsHuman = this.humanControlled(best);
    if (bestIsHuman) timeRating *= 0.8;
    if (this.humanControlled(designated)) timeRating /= 0.8;
    // The opposition's figure is this tick's; the original reads the previous tick's, because
    // it does this before recomputing anybody's time to the ball. Ten milliseconds against a
    // hundred-millisecond margin.
    const opponentTimeMs = this.opponentTeam(team.id).timeNeededToGetToBallMs;
    if (!bestIsHuman && designatedTimeMs < opponentTimeMs - 100) {
      timeRating += 0.2;
      timeRating *= 1.2;
    }
    if (timeRating < 0.8) team.designatedPlayer = best;
  }

  /** `Player::SendOff` (`player.cpp:489-520`). */
  private processDismissals(): void {
    for (const player of this.players) {
      if (player.cards > 1 && this.timeMs >= player.cardEffectiveTimeMs) this.sendOff(player);
    }
  }

  private sendOff(player: MatchPlayer): void {
    const team = this.team(player.teamId);
    const index = team.players.indexOf(player);
    if (index === -1) return;

    const replacement = team.players.find((candidate) => candidate !== player && candidate.active);
    if (player.role === 'GK' && replacement) {
      replacement.formationEntry = cloneFormationEntry(player.formationEntry);
      replacement.dynamicFormationEntry = cloneFormationEntry(player.formationEntry);
    }

    player.active = false;
    // Purely presentational, and deliberately set in the same synchronous call as everything
    // else here: nothing below reads it, so it cannot slow down or reorder the gameplay
    // bookkeeping this method exists for. See `isDeparting`.
    player.dismissedAtMs = this.timeMs;
    player.hasPossession = false;
    player.hasBestPossession = false;
    player.hasUniquePossession = false;
    team.players.splice(index, 1);
    if (team.designatedPlayer === player && replacement) team.designatedPlayer = replacement;
    if (team.lastTouchPlayer === player) team.lastTouchPlayer = null;
    if (this.ballRetainer === player) this.ballRetainer = null;
    this.referee.playerDismissed(player);

    if (this.designatedPossessionPlayer === player) {
      this.designatedPossessionPlayer = replacement ?? this.opponentTeam(player.teamId).designatedPlayer;
    }

    if (team.players.length <= 6) {
      this.endMatch();
    }
  }

  /**
   * Give a player an injury — tranche 2.4 of docs/design/violence-and-variety.md. A no-op on
   * a player who already carries one (nothing here heals him, and the first injury holds for
   * the rest of the match) or who is already off the pitch.
   *
   * A serious injury (3) REUSES `sendOff` OUTRIGHT rather than a second dismissal path: it
   * already removes him from his side, hands his duties on, releases the ball retainer if he
   * held it and ends the match at seven players, which is exactly what carrying a man off
   * needs and nothing more. Note what it does NOT do — book a card, or tell the referee this
   * was a card-worthy incident. An injury is not a foul; the two are related only in that a
   * foul's collision is one of the things that can cause one (`assessInjury` below).
   */
  applyInjury(player: MatchPlayer, severity: InjurySeverity, timeMs: number): void {
    if (player.injury !== null || !player.active) return;
    player.setInjury({ severity, timeMs });
    if (severity === 3) this.sendOff(player);
  }

  /**
   * Should this collision leave the victim injured, and how badly? Tranche 2.4. Nothing like
   * this exists in the original, so the shape is kept to what the plan actually asks for: a
   * severity threshold on the collision itself, and a much likelier one for a landed punch or
   * flying kick — `tripper.violentChallengeUntilMs`, the same field the referee reads
   * independently at `referee.ts:293` to know the SAME collision was violent rather than an
   * honest challenge.
   *
   * The chance is a hash of the tick and the victim's id, exactly the shape the AI's own pass
   * and shot coin flips already use (`hashedRandom`, `src/core/random.ts`) — never the
   * platform's random number generator, so a fixture still replays exactly on the same seed,
   * and two different players hit on the same tick do not share an answer.
   *
   * PUBLIC FOR THE SAME REASON `Referee.offenceNotice` IS. A punch has no collision in it at
   * all — no two men's legs overlap — so the collision pass never reaches it, and a landed
   * punch that could not hurt anybody is half the act. It calls this directly, exactly as it
   * tells the referee directly. Everything else comes in through `handleCollision`.
   */
  assessInjury(victim: MatchPlayer, tripper: MatchPlayer, tripType: number): void {
    if (victim.injury !== null || !victim.active) return;
    // A type-1 trip is a stumble, not a "collision above a severity threshold" — only 2 and
    // 3 (the two the referee itself treats as fouls, `collision.ts`'s own comment on the
    // restored `2`) carry any injury risk at all.
    if (tripType < 2) return;
    const violent = tripper.violentChallengeUntilMs > this.timeMs;
    let chance = violent ? INJURY_CHANCE_VIOLENT : tripType === 3 ? INJURY_CHANCE_TRIP3 : INJURY_CHANCE_TRIP2;
    if (this.violence === 'anything-goes') chance *= ANYTHING_GOES_INJURY_MULTIPLIER;
    chance *= 1 + (RESILIENCE_REFERENCE - victim.physics.stat('mental_resilience')) * RESILIENCE_INJURY_SLOPE;

    const tick = (this.timeMs / 10) | 0;
    if (hashedRandom(tick, victim.id, INJURY_CHANCE_SALT, this.randomSeed) >= chance) return;

    const severityRoll = hashedRandom(tick, victim.id, INJURY_SEVERITY_SALT, this.randomSeed);
    let cumulative = 0;
    let severity: InjurySeverity = 1;
    for (const level of [1, 2, 3] as const) {
      cumulative += INJURY_SEVERITY_SHARE[level];
      if (severityRoll < cumulative) {
        severity = level;
        break;
      }
    }
    this.applyInjury(victim, severity, this.timeMs);
  }

  private calculateBestPossessionTeamId(): void {
    if (this.ballRetainer !== null) {
      this.bestPossessionTeamId = this.ballRetainer.teamId === 0 ? 0 : 1;
      return;
    }
    const a = this.teams[0].timeNeededToGetToBallMs;
    const b = this.teams[1].timeNeededToGetToBallMs;
    this.bestPossessionTeamId = a < b ? 0 : a > b ? 1 : -1;
  }

  private closestOpponentDistanceFor(player: MatchPlayer): number {
    let closest = 10000;
    for (const opponent of this.opponentTeam(player.teamId).players) {
      const distance = opponent.position.distanceTo(player.position);
      if (distance < closest) closest = distance;
    }
    return closest;
  }

  /** `Player::AllowLastDitch` for a given player. */
  allowLastDitch(player: MatchPlayer, includingPossessionAmount = true): boolean {
    return allowLastDitch(
      player.timeNeeded,
      this.team(player.teamId).possessionAmount,
      includingPossessionAmount,
    );
  }

  /**
   * `HumanoidBase::_HighOrBouncyBall` (humanoidbase.cpp:1089-1100) — is this ball awkward?
   *
   * An awkward ball relaxes the selector: a player is allowed a clip that goes somewhere
   * other than where he wanted, because getting a foot to it at all is the priority.
   */
  highOrBouncyBall(): boolean {
    if (this.ball.predict(10, _probe).z > 0.3) return true;
    if (this.ball.predict(80, _probe).z > 0.3) return true;
    return Math.abs(this.ball.momentum.z) > 1.0;
  }
}

/**
 * Whichever of this side's men is quickest to the ball right now — `Team::GetBestPossessionPlayer`
 * (team.cpp:211-227). Not necessarily the man the side has actually sent for it; a tie goes to
 * whoever appears first in the squad, as it does there.
 */
function bestChaser(team: TeamState): MatchPlayer {
  let best = team.players[0]!;
  for (const player of team.players) {
    if (player.timeNeeded.usualMs < best.timeNeeded.usualMs) best = player;
  }
  return best;
}

/**
 * Where the football clock stands at the start of each period (`match.cpp:688-692`).
 *
 * There is no entry for the pre-match phase, and the original has no branch for it either:
 * the clock is already at nought and nothing should touch it.
 */
const PHASE_START_MS: Partial<Record<MatchPhase, number>> = {
  [MatchPhase.FirstHalf]: 0,
  [MatchPhase.SecondHalf]: 2_700_000,
  [MatchPhase.FirstExtraTime]: 5_400_000,
  [MatchPhase.SecondExtraTime]: 6_300_000,
  [MatchPhase.Penalties]: 7_200_000,
};

function defaultFormationEntry(role: PlayerRole): FormationEntry {
  const anchor = ROLE_ANCHOR[role];
  return {
    role,
    databasePosition: new Vector3(anchor[0], anchor[1], 0),
    position: new Vector3(anchor[0], anchor[1], 0),
  };
}

function cloneFormationEntry(entry: FormationEntry): FormationEntry {
  return {
    role: entry.role,
    databasePosition: entry.databasePosition.clone(),
    position: entry.position.clone(),
  };
}

const _probe = new Vector3();
